import { resolveUserVersion } from "../api/userVersion";
import getEnvConfig from "./config";
import {isAlpha, isBeta} from "./environmentUtils";

export const createUrlInterceptor =
  (domain: string, signInUIDomain: string) => (url: string) => {
    const SELF = "_self";
    const loginRequestUrl = `https://${domain}/oauth2/authorize`;
    let windowProxy;

    if (signInUIDomain && url.startsWith(loginRequestUrl)) {
      const signInUrl = url.replace(
        loginRequestUrl,
        `https://${signInUIDomain}/login`
      );
      windowProxy = window.open(signInUrl, SELF);
    } else if (url.includes('/logout')) {
      window.location.href = url.replace('/logout?', `/logout?response_type=code&`);
      windowProxy = window;
    } else {
      windowProxy = window.open(url, SELF);
    }
    return windowProxy ? Promise.resolve(windowProxy) : Promise.reject();
  };

export const isCallbackUrl = () => {
  let href = window.location.href;
  return href && href.includes("callback");
};

export const isReleaseCandidate = () => {
  let search = window.location.search;
  return search && search.includes("rc=true");
};

export const injectTokenManually = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const injectTokenParam = urlParams.get("injectTokenManually");
  return (isAlpha() || isBeta()) && injectTokenParam !== null;
}

export const getGuestIdp = () => {
  const envConfig = getEnvConfig();
  if (envConfig.gandalf_auth) {
    return null;
  }
  return envConfig.guest_idp;
}

export const getDevBuildId = () => {
  let devBuild = "";

  const urlParams = new URLSearchParams(window.location.search);
  devBuild = urlParams.get("devBuild") || "";

  const regex = new RegExp("^[0-9]{4,14}$");
  return regex.test(devBuild) ? devBuild : null;
};

export const getQueryStrings = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return Object.fromEntries(urlParams.entries());
};

export const getWebGlLocation = async (authToken: string) => {
  const devBuildId = getDevBuildId();

  const env = getEnvConfig();
  if (env.enable_webgl_loader) {
    if (env.webgl_location_config_name) {
      return getWebGlLocationFromConfig(devBuildId, authToken);
    } else if (env.webgl_location_url) {
      return getWebGlLocationFromUrl(env.webgl_location_url, devBuildId);
    }

    throw new Error("Environment configuration error: webgl location not found");
  }

  return undefined;

};

const getWebGlLocationFromConfig = async (devBuildId: string | null, authToken: string): Promise<string> => {
  if (isReleaseCandidate() && isAlpha()) {
    console.log("will load rc_location");
    return getPuConfigUrl() + "/config/rc_location";
  } else if (devBuildId && isAlpha()) {
    console.log(`will load webgl_pr_${devBuildId}_location`);
    return (
      getPuConfigUrl() + `/config/webgl_pr_${devBuildId}_location`
    );
  } else {
    const config = getEnvConfig()
    let configName = config.webgl_location_config_name;

    if(config.webgl_historic_location_by_user_version) {
      console.log("Will check user version...")
      const userVersion = await resolveUserVersion(authToken);
      console.log(`User version: ${userVersion}`)
      if(config.webgl_historic_location_by_user_version[userVersion]) {
        configName = config.webgl_historic_location_by_user_version[userVersion].webgl_location_config_name;
      }
    }

    console.log(`Using config name: ${configName}`)
    return getPuConfigUrl() + "/config/" + configName;
  }
}

const getWebGlLocationFromUrl = (url: string, devBuildId: string | null): string => {
  if (devBuildId && isAlpha()) {
    return `${url}/pr/${devBuildId}.json`;
  }
  return `${url}/WebGLConfig.json`
}

export const getPuConfigUrl = () => {
  return window.GameEnvironment.config.pu_config_url;
};

export const getPuPlayerProgressUrl = () => {
  return window.GameEnvironment.config.pu_player_progress_url;
};

export const getPuFrontendLogsUrl = () => {
  return window.GameEnvironment.config.pu_frontend_logs_url;
};

export const getPuPromotionUrl = () => {
  return window.GameEnvironment.config.pu_promotion_url;
};

export const getPuCardDeckPlayerUrl = () => {
  return window.GameEnvironment.config.pu_card_deck_player_url;
};

export const getLogoutUrl = () => {
  const location = window.location;
  if (typeof location.origin === "undefined") {
    return location.protocol + "//" + location.host;
  }
  return location.origin;
};

export const getRedirectUrl = () => {
  const location = window.location;
  if (typeof location.origin === "undefined") {
    return location.protocol + "//" + location.host + "/callback";
  }
  if (isEventAuth()) {
    return location.origin + "/eventcallback";
  }
  return location.origin + "/callback";
};

export const isEventAuth = () => {
  const path = window.location.pathname;
  return path.startsWith("/event") || path.startsWith("/eventcallback");
}
